import { useEffect, useMemo, useState } from "react";
import { Slider as RadixSlider } from "radix-ui";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import PropTypes from "prop-types";
import "./Slider.scss";

export const Slider = ({
	value,
	defaultValue,
	onValueCommit,
	onValueChange,
	min,
	max,
	formatValue,
	step = 1,
	...props
}) => {
	const isControlled = value !== undefined;

	const initial = useMemo(() => {
		const v = isControlled ? value : defaultValue;

		if (Array.isArray(v) && v.length > 0) {
			return v;
		}

		return [max];
	}, [isControlled, value, defaultValue, max]);

	const [uiValue, setUiValue] = useState(initial);

	useEffect(() => {
		if (isControlled) {
			setUiValue(Array.isArray(value) && value.length ? value : [max]);
		}
	}, [isControlled, value, min]);

	const display = v => {
		if (formatValue) {
			return String(formatValue(v));
		}
		return String(v);
	};

	const handleCommit = newValue => {
		const cb = onValueCommit ?? onValueChange;

		if (cb) {
			cb(newValue);
		}

		if (!isControlled) {
			setUiValue(newValue);
		}
	};

	return (
		<div className="slider-wrapper">
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				component="span"
				className="slider-boundary"
			>
				{display(min)}
			</Typography>

			<RadixSlider.Root
				className="slider-root"
				value={uiValue}
				onValueChange={setUiValue}
				onValueCommit={handleCommit}
				min={min}
				max={max}
				step={step}
				minStepsBetweenThumbs={1}
				{...props}
			>
				<RadixSlider.Track className="slider-track">
					<RadixSlider.Range className="slider-range" />
				</RadixSlider.Track>

				{uiValue.map((v, index) => {
					return (
						<RadixSlider.Thumb className="slider-thumb" key={`slider-thumb-${index}`}>
							{v !== min && v !== max && (
								<Typography
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
									component="span"
									className="slider-tooltip"
								>
									{display(v)}
								</Typography>
							)}
						</RadixSlider.Thumb>
					);
				})}
			</RadixSlider.Root>

			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				component="span"
				className="slider-boundary"
			>
				{display(max)}
			</Typography>
		</div>
	);
};

Slider.propTypes = {
	value: PropTypes.arrayOf(PropTypes.number),
	defaultValue: PropTypes.arrayOf(PropTypes.number),
	onValueCommit: PropTypes.func,
	onValueChange: PropTypes.func,
	min: PropTypes.number.isRequired,
	max: PropTypes.number.isRequired,
	step: PropTypes.number,
	formatValue: PropTypes.func,
};
